import { cva, type VariantProps } from 'class-variance-authority';
import { MdClose } from 'react-icons/md';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';

const panelVariants = cva(
  // `max-h` : une boîte plus haute que l'écran verrait son pied — donc ses
  // boutons — sortir du cadre, sans rien pour y accéder. Passé cette hauteur,
  // c'est le contenu qui défile (voir le corps ci-dessous) ; l'en-tête et la
  // barre d'actions restent en place.
  'relative flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-card bg-surface shadow-card',
  {
    variants: {
      size: {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-2xl',
        '2xl': 'max-w-4xl',
      },
    },
    defaultVariants: { size: 'lg' },
  },
);

export type ModalProps = VariantProps<typeof panelVariants> & {
  open: boolean;
  /** Absent = boîte non fermable (onboarding obligatoire, par ex.). */
  onClose?: () => void;
  title?: string;
  /**
   * Taille du titre. `md` (par défaut) convient à une question ; `lg` à une
   * confirmation — « Votre message est prêt » porte la nouvelle, un titre
   * de la même taille qu'un champ de formulaire la disait trop bas.
   */
  titleSize?: 'md' | 'lg';
  description?: ReactNode;
  /** Icône affichée au-dessus du titre. */
  icon?: ReactNode;
  /** Barre d'actions en bas de la boîte. */
  footer?: ReactNode;
  /** Alignement de l'en-tête. `center` convient aux parcours guidés. */
  align?: 'start' | 'center';
  /** Colonne pleine hauteur à côté du contenu (visuel, illustration). Masquée sous `sm`. */
  aside?: ReactNode;
  /**
   * Côté de la colonne `aside`. `end` (par défaut) la pose à droite, après le
   * formulaire ; `start` à gauche, quand elle montre le résultat de ce qu'on
   * saisit — l'aperçu se lit alors avant le champ qui l'alimente.
   */
  asideSide?: 'start' | 'end';
  children?: ReactNode;
  className?: string;
};

/** Boîte de dialogue centrée, fond flouté. */
export function Modal({
  open,
  onClose,
  title,
  titleSize = 'md',
  description,
  icon,
  footer,
  children,
  size,
  align = 'start',
  aside,
  asideSide = 'end',
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  /**
   * `onClose` est presque toujours une fonction recréée à chaque rendu du
   * parent. La garder dans les dépendances de l'effet le relancerait à chaque
   * frappe, et le `focus()` ci-dessous arracherait le curseur du champ en
   * cours de saisie. La ref donne toujours la dernière version sans faire
   * partie des dépendances.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Dépend de `open` seul : ces effets accompagnent l'ouverture, pas chaque rendu.
  useEffect(() => {
    if (!open) return;

    // Bloque le défilement de la page derrière la boîte.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown);

    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-brand-900/40 backdrop-blur-md"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          'animate-fade-in-up z-10 outline-none',
          panelVariants({ size }),
          // `row-reverse` plutôt qu'un `order` : la colonne reste rendue après
          // le formulaire dans le DOM — c'est lui qu'on veut atteindre en
          // premier au clavier, l'aperçu ne se manipule pas.
          aside && (asideSide === 'start' ? 'sm:flex-row-reverse' : 'sm:flex-row'),
          className,
        )}
      >
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="absolute right-4 top-4 rounded-lg p-1.5 text-content-subtle transition-colors hover:bg-surface-muted hover:text-content"
          >
            <MdClose className="size-5" aria-hidden="true" />
          </button>
        ) : null}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {title || icon || description ? (
            <div
              className={cn(
                'flex flex-col gap-4 px-8 pb-2 pt-8',
                align === 'center' && 'items-center text-center',
                // Sans corps, l'en-tête est seul au-dessus de la barre
                // d'actions : le `pb-2` qui le sépare de son contenu collait
                // alors la description aux boutons.
                !children && 'pb-6',
              )}
            >
              {icon}
              <div className="flex flex-col gap-2">
                {/* `text-balance` répartit les lignes à égalité : sans lui, une
                    question un peu longue laissait deux mots seuls sur la
                    seconde ligne. */}
                <h2
                  id={titleId}
                  className={cn(
                    'font-semibold text-balance text-content',
                    titleSize === 'lg' ? 'text-2xl' : 'text-xl',
                  )}
                >
                  {title}
                </h2>
                {description ? (
                  <p className="text-sm leading-relaxed text-content-muted">{description}</p>
                ) : null}
              </div>
            </div>
          ) : null}

          {children ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">{children}</div>
          ) : null}

          {footer ? (
            // `mt-auto` : sans corps, rien ne prend la hauteur restante — la
            // barre venait se coller sous l'en-tête, en haut d'une colonne
            // dont la hauteur est celle de l'`aside`. Sans effet quand il y a
            // un corps, déjà en `flex-1`.
            <div className="mt-auto flex flex-col gap-3 border-t border-border-subtle bg-surface-muted/60 px-8 py-5">
              {footer}
            </div>
          ) : null}
        </div>

        {aside ? (
          <div
            className={cn(
              'hidden w-1/2 shrink-0 items-center overflow-hidden bg-surface-muted sm:flex',
              // Le filet sépare toujours la colonne du formulaire : il change
              // donc de bord avec elle.
              asideSide === 'start'
                ? 'border-r border-border-subtle'
                : 'border-l border-border-subtle',
            )}
          >
            {aside}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export { panelVariants as modalPanelVariants };
